import { Action, Reducer } from 'redux';
import { delay } from 'redux-saga/effects';
import { fork, call, takeEvery, put, all, select } from 'redux-saga/effects';
import { createSelector } from 'reselect';

import { ITableConfig } from '../components/common/table';

import Api from '../api';

import { IRootState } from './index';
import Project from '../models/Project';
import ProjectType from '../models/ProjectType';
import ProjectModel from '../models/Project';

import { actionCreators as layoutActionCreators } from '../reducers/Layout';

import { selectors as authSelectors } from '../reducers/Auth';
import PagedResultSet from '../models/common/PagedResultSet';
import { ODataOptions } from '../util/ODataBuilder';
import { replace } from 'connected-react-router';
import LeaseClauseType from 'src/models/LeaseClauseType';
import OwnerBusiness from 'src/models/tract_detail/add_ownership_revised/OwnerBusiness';
import companyOwnerData from 'src/models/companyOwnerData.json';
import { SelectItem } from 'src/models/common/SelectItem';

// Store
export interface IProjectState {
    isProjectsLoading: boolean;
    projects: PagedResultSet<Project>;
    isProjectTypesLoading: boolean;
    createProjectLoading: boolean;
    projectTypes: ProjectType[] | undefined;
    tractTypes: SelectItem[] | undefined;
    clientDetailProjectTableConfig: ITableConfig;
    leaseClauseTypes: Array<LeaseClauseType>;
    currentProject: Project;
}

const initialState: IProjectState = {
    isProjectsLoading: false,
    isProjectTypesLoading: false,
    projects: undefined,
    createProjectLoading: false,
    projectTypes: undefined,
    tractTypes: undefined,
    clientDetailProjectTableConfig: {
        pageNumber: 1,
        rowCount: 10,
        idField: 'projectNumber',
        columns: [
            { name: 'Name', mapping: 'name' },
            { name: 'CloakName', mapping: 'cloakName' },
            { name: 'Type', mapping: 'projectType.name' },
        ],
    },
    leaseClauseTypes: undefined,
    currentProject: undefined,
};

// Action Types
export enum actionTypes {
    INITIALIZE_PROJECTS = '[project] INITIALIZE_PROJECTS',
    GET_PROJECTS = '[project] GET_PROJECTS',
    GET_PROJECTS_SUCCESS = '[project] GET_PROJECTS_SUCCESS',
    REFRESH_INTEGRATED_PROJECTS = '[project] REFRESH_INTEGRATED_PROJECTS',
    REFRESH_INTEGRATED_PROJECTS_SUCCESS = '[project] REFRESH_INTEGRATED_PROJECTS_SUCCESS',
    GET_PROJECT = '[project] GET_PROJECT',
    GET_PROJECT_SUCCESS = '[project] GET_PROJECT_SUCCESS',
    GET_PROJECT_TYPES = '[project] GET_PROJECT_TYPES',
    GET_PROJECT_TYPES_SUCCESS = '[project] GET_PROJECT_TYPES_SUCCESS',
    GET_TRACT_TYPES = '[project] GET_TRACT_TYPES',
    GET_TRACT_TYPES_SUCCESS = '[project] GET_TRACT_TYPES_SUCCESS',
    CREATE_PROJECT = '[project] CREATE_PROJECT',
    CREATE_PROJECT_SUCCESS = '[project] CREATE_PROJECT_SUCCESS',
    UPDATE_PROJECT = '[project] UPDATE_PROJECT',
    UPDATE_PROJECT_SUCCESS = '[project] UPDATE_PROJECT_SUCCESS',
    GET_LEASE_CLAUSE_TYPES = '[project] GET_LEASE_CLAUSE_TYPES',
    GET_LEASE_CLAUSE_TYPES_SUCCESS = '[project] GET_LEASE_CLAUSE_TYPES_SUCCESS',
    SHOW_REAL_SUCCESS = '[project] SHOW_REAL_SUCCESS',
    HIDE_REAL = '[project] HIDE_REAL',
}

// Action Creator
export const actionCreators = {
    initializeProjects: () => {
        return { type: actionTypes.INITIALIZE_PROJECTS };
    },
    getProjects: (clientNumber: number, options: ODataOptions, resetPage?: boolean) => {
        return { type: actionTypes.GET_PROJECTS, payload: { clientNumber, options, resetPage } };
    },
    getProjectsSuccess: (projects: PagedResultSet<Project>) => {
        return { type: actionTypes.GET_PROJECTS_SUCCESS, payload: { projects } };
    },
    getProject: (clientNumber: number, projectNumber: number) => {
        return { type: actionTypes.GET_PROJECT, payload: { clientId: clientNumber, projectId: projectNumber } };
    },
    getProjectSuccess: (project: Project) => {
        return { type: actionTypes.GET_PROJECT_SUCCESS, payload: project };
    },
    refreshIntegratedProjects: (clientNumber: number) => {
        return { type: actionTypes.REFRESH_INTEGRATED_PROJECTS, payload: { clientNumber: clientNumber } };
    },
    refreshIntegratedProjectsSuccess: (projects: PagedResultSet<Project>) => {
        return { type: actionTypes.REFRESH_INTEGRATED_PROJECTS_SUCCESS, payload: { projects } };
    },
    getProjectTypes: (options: ODataOptions) => {
        return { type: actionTypes.GET_PROJECT_TYPES, payload: options };
    },
    getProjectTypesSuccess: (projectTypes: PagedResultSet<ProjectType>) => {
        return { type: actionTypes.GET_PROJECT_TYPES_SUCCESS, payload: projectTypes };
    },
    getTractTypes: (clientNumber: number) => {
        return { type: actionTypes.GET_TRACT_TYPES, payload: { clientNumber } };
    },
    getTractTypesSuccess: (tractTypes: SelectItem[]) => {
        return { type: actionTypes.GET_TRACT_TYPES_SUCCESS, payload: tractTypes };
    },
    createProject: (project: ProjectModel) => {
        return { type: actionTypes.CREATE_PROJECT, payload: project };
    },
    updateProject: (project: ProjectModel) => {
        return { type: actionTypes.UPDATE_PROJECT, payload: project };
    },
    createProjectSuccess: () => {
        return { type: actionTypes.CREATE_PROJECT_SUCCESS };
    },
    getLeaseClauseTypes: (clientNumber: number, projectId: number) => {
        return { type: actionTypes.GET_LEASE_CLAUSE_TYPES, payload: { clientNumber: clientNumber, projectId: projectId } };
    },
    getLeaseClauseTypesSuccess: (clauseTypes: Array<LeaseClauseType>) => {
        return { type: actionTypes.GET_LEASE_CLAUSE_TYPES_SUCCESS, payload: clauseTypes };
    },
};

// Reducer
const reducer: Reducer<IProjectState> = (state: IProjectState = initialState, action: Action): IProjectState => {
    switch (action.type) {
        case actionTypes.INITIALIZE_PROJECTS:
            return {
                ...state,
                projects: initialState.projects,
            };
        case actionTypes.GET_PROJECT:
            return {
                ...state,
                isProjectsLoading: true,
            };
        case actionTypes.GET_PROJECTS:
            return {
                ...state,
                isProjectsLoading: true,
            };
        case actionTypes.GET_PROJECT_SUCCESS:
            const getProjectPayload = (action as { type: string; payload: Project }).payload;
            const projectResultSet = new PagedResultSet<Project>();
            projectResultSet.items.push(getProjectPayload);
            return {
                ...state,
                currentProject: getProjectPayload,
                projects: projectResultSet,
                isProjectsLoading: true,
            };
        case actionTypes.GET_PROJECTS_SUCCESS: {
            const getProjectsPayload = (action as { type: string; payload: { projects: PagedResultSet<Project>; resetPage: boolean } }).payload;
            if (getProjectsPayload.resetPage) {
                return {
                    ...state,
                    projects: getProjectsPayload.projects,
                    isProjectsLoading: false,
                };
            } else if (state.projects === undefined || state.projects.pageNumber < getProjectsPayload.projects.pageNumber) {
                const newProjects = { ...state.projects };
                newProjects.pageNumber = getProjectsPayload.projects.pageNumber;
                newProjects.pageSize = getProjectsPayload.projects.pageSize;
                newProjects.totalRecords = getProjectsPayload.projects.totalRecords;
                if (!newProjects.items) {
                    newProjects.items = [];
                }

                newProjects.items = newProjects.items.concat(getProjectsPayload.projects.items);

                return {
                    ...state,
                    projects: newProjects,
                    isProjectsLoading: false,
                };
            }

            return state;
        }

        case actionTypes.REFRESH_INTEGRATED_PROJECTS_SUCCESS: {
            const getProjectsPayload = (action as { type: string; payload: { projects: PagedResultSet<Project>; resetPage: boolean } }).payload;
            if (getProjectsPayload.resetPage) {
                return {
                    ...state,
                    projects: getProjectsPayload.projects,
                    isProjectsLoading: false,
                };
            } else if (state.projects === undefined || state.projects.pageNumber < getProjectsPayload.projects.pageNumber) {
                const newProjects = { ...state.projects };
                newProjects.pageNumber = getProjectsPayload.projects.pageNumber;
                newProjects.pageSize = getProjectsPayload.projects.pageSize;
                newProjects.totalRecords = getProjectsPayload.projects.totalRecords;
                if (!newProjects.items) {
                    newProjects.items = [];
                }

                newProjects.items = newProjects.items.concat(getProjectsPayload.projects.items);

                return {
                    ...state,
                    projects: newProjects,
                    isProjectsLoading: false,
                };
            }

            return state;
        }

        case actionTypes.GET_PROJECT_TYPES:
            return {
                ...state,
                projectTypes: initialState.projectTypes,
                isProjectTypesLoading: true,
            };
        case actionTypes.GET_PROJECT_TYPES_SUCCESS: {
            const payload = (action as { type: string; payload: ProjectType[] }).payload;
            return {
                ...state,
                projectTypes: payload,
                isProjectTypesLoading: false,
            };
        }
        case actionTypes.GET_TRACT_TYPES:
            return {
                ...state,
                tractTypes: initialState.tractTypes
            };
        case actionTypes.GET_TRACT_TYPES_SUCCESS: {
            const payload = (action as { type: string; payload: SelectItem[] }).payload;
            return {
                ...state,
                tractTypes: payload,
            };
        }
        case actionTypes.CREATE_PROJECT: {
            return {
                ...state,
                createProjectLoading: true,
            };
        }
        case actionTypes.CREATE_PROJECT_SUCCESS: {
            return {
                ...state,
                createProjectLoading: false,
            };
        }
        case actionTypes.UPDATE_PROJECT: {
            return {
                ...state,
                createProjectLoading: true,
            };
        }
        case actionTypes.UPDATE_PROJECT_SUCCESS: {
            return {
                ...state,
                createProjectLoading: false,
            };
        }
        case actionTypes.GET_LEASE_CLAUSE_TYPES:
            return {
                ...state,
                leaseClauseTypes: initialState.leaseClauseTypes,
            };
        case actionTypes.GET_LEASE_CLAUSE_TYPES_SUCCESS: {
            const clauseTypes = (action as { type: string; payload: Array<LeaseClauseType> }).payload;

            return {
                ...state,
                leaseClauseTypes: clauseTypes,
            };
        }
        default:
            return state;
    }
};

export default reducer;

const projectsSelector = (state: IRootState): PagedResultSet<Project> | undefined => state.project.projects;
const projectSelector = (clientId: number, projectId: number) =>
    createSelector(projectsSelector, (projects) => {
        let p = undefined;
        if (projects) {
            p = projects.items.filter(
                (project: Project) =>
                    project.cloakProjectNumber === Number.parseInt(projectId + '', 10) ||
                    project.projectNumber === Number.parseInt(projectId + '', 10)
            )[0];
        }
        return p;
    });

const projectTypesSelector = (state: IRootState): ProjectType[] | undefined => state.project.projectTypes;
const projectTypeSelector = (id: number) =>
    createSelector(projectTypesSelector, (projectTypes) => {
        return projectTypes ? projectTypes.filter((projectType: ProjectType) => projectType.id === Number.parseInt(id + '', 10))[0] : undefined;
    });

const tractTypesSelector = (state: IRootState): SelectItem[] | undefined => state.project.tractTypes;
const tractTypeSelector = (id: number) =>
    createSelector(tractTypesSelector, (tractTypes) => {
        return tractTypes ? tractTypes.filter((tractType: SelectItem) => tractType.id === Number.parseInt(id + '', 10))[0] : undefined;
    });

export const selectors = {
    isLoading: (state: IRootState): boolean => state.project.isProjectsLoading || state.layout.initialLoading,
    isProjectTypesLoading: (state: IRootState): boolean => state.project.isProjectTypesLoading || state.layout.initialLoading,
    projects: projectsSelector,
    project: projectSelector,
    projectTypes: projectTypesSelector,
    projectType: projectTypeSelector,
    tractTypes: tractTypesSelector,
    tractType: tractTypeSelector,
    clientDetailProjectTableConfig: (state: IRootState): ITableConfig => state.project.clientDetailProjectTableConfig,
};

// Side-Effects
const sagas = {
    getProjects: function* (action: { type: string; payload: { clientNumber: number; options: ODataOptions; resetSearch: boolean } }): unknown {
        console.log('at saga', action.payload.options);
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Project.getAll, action.payload.clientNumber, action.payload.options, jwt), delay(200)]);
        yield put(actionCreators.getProjectsSuccess(result));
    },
    getProject: function* (action: { type: string; payload: { clientId: number; projectId: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        let project: Project;
        const clientId = action.payload.clientId;
        const projectId = action.payload.projectId;
        project = yield select(selectors.project(clientId, projectId));
        if (project) {
            yield put(actionCreators.getProjectSuccess(project));
        } else {
            const jwt = yield select(authSelectors.jwt);
            const [result] = yield all([call(Api.Project.get, clientId, projectId, jwt), delay(200)]);
            yield put(actionCreators.getProjectSuccess(result));
        }
    },
    refreshIntegratedProject: function* (action: { type: string; payload: { clientNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Project.refreshIntegrated, action.payload.clientNumber, jwt), delay(200)]);
        yield put(actionCreators.refreshIntegratedProjectsSuccess(result));
        yield put(replace('/'));
        yield put(replace(`/clients/${action.payload.clientNumber}`));
    },
    getProjectTypes: function* (action: { type: string; payload: ODataOptions }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.ProjectType.getAll, action.payload, jwt), delay(200)]);
        yield put(actionCreators.getProjectTypesSuccess(result));
    },
    getTractTypes: function* (action: { type: string; payload: { clientNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Project.getTractTypes, action.payload.clientNumber, jwt), delay(200)]);
        yield put(actionCreators.getTractTypesSuccess(result));
    },
    createProject: function* (action: { type: string; payload: ProjectModel }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const result = yield call(Api.Project.create, action.payload, jwt);
        // TODO: Update the companyOwner for it to be more dynamic and not hard coded to the project.
        const companyOwner = new OwnerBusiness(companyOwnerData as OwnerBusiness);

        // create dudley owner
        const dudleyOwnerId = yield call(Api.Owner.createBusinessOwner, action.payload.client.id, result.id, companyOwner, jwt);

        // update project with owner id's
        const updatedProject = action.payload;
        updatedProject.defaultOwnerLookUpId = dudleyOwnerId;
        updatedProject.id = result.id;
        updatedProject.projectNumber = result.id;
        updatedProject.addCounties = [];

        yield put(actionCreators.updateProject(updatedProject));
        yield put(replace(`/clients/${action.payload.client.id}/projects/${result.id}`));
    },
    updateProject: function* (action: { type: string; payload: ProjectModel }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        yield call(Api.Project.update, action.payload, jwt);
        yield put(replace(`/clients/${action.payload.client.clientNumber}/projects/${action.payload.projectNumber}/settings`));
        yield put(actionCreators.createProjectSuccess());
    },
    getClauseTypes: function* (action: { type: string; payload: { clientNumber: number; projectId: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const result = yield call(Api.LeaseClauseType.getAll, action.payload.clientNumber, action.payload.projectId, jwt);
        yield put(actionCreators.getLeaseClauseTypesSuccess(result));
    },
};

const watches = {
    getProjects: function* () {
        yield takeEvery(actionTypes.GET_PROJECTS, sagas.getProjects);
    },
    getProject: function* () {
        yield takeEvery(actionTypes.GET_PROJECT, sagas.getProject);
    },
    getProjectTypes: function* () {
        yield takeEvery(actionTypes.GET_PROJECT_TYPES, sagas.getProjectTypes);
    },
    getTractTypes: function* () {
        yield takeEvery(actionTypes.GET_TRACT_TYPES, sagas.getTractTypes);
    },
    createProject: function* () {
        yield takeEvery(actionTypes.CREATE_PROJECT, sagas.createProject);
    },
    updateProject: function* () {
        yield takeEvery(actionTypes.UPDATE_PROJECT, sagas.updateProject);
    },
    refreshIntegratedProject: function* () {
        yield takeEvery(actionTypes.REFRESH_INTEGRATED_PROJECTS, sagas.refreshIntegratedProject);
    },
    getLeaseClauseTypes: function* () {
        yield takeEvery(actionTypes.GET_LEASE_CLAUSE_TYPES, sagas.getClauseTypes);
    },
};

export function* saga() {
    yield fork(watches.getProjects);
    yield fork(watches.getProject);
    yield fork(watches.getProjectTypes);
    yield fork(watches.getTractTypes);
    yield fork(watches.createProject);
    yield fork(watches.updateProject);
    yield fork(watches.refreshIntegratedProject);
    yield fork(watches.getLeaseClauseTypes);
}



// WEBPACK FOOTER //
// ./src/reducers/Project.ts